
import React, { useState, useRef, useEffect } from 'react';
import { StudentData } from './types';
import { SecurityPatterns } from './SecurityPatterns';
import { Printer, Sliders, Droplet, Palette, Type, ImagePlus, Trash2, ChevronDown, UserSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { IdCardConfig } from './IdCardTypes';
import { IdCardRenderer } from './IdCardRenderer';

interface IdCardDesignerProps {
  data: StudentData;
  onUpdateData?: (newData: StudentData) => void;
  initialConfig?: IdCardConfig;
  onConfigChange?: (config: IdCardConfig) => void;
}

// ==========================================
// 1. CONSTANTS & CONFIGURATIONS
// ==========================================

const ID_PATTERNS = [
  { id: 'none', name: 'No Pattern' },
  { id: 'watercolor', name: 'Ink Wash' },
  { id: 'brush', name: 'Rough Brush' },
  { id: 'waves-soft', name: 'Soft Smoke' },
  { id: 'splatter', name: 'Ink Splatter' },
  { id: 'marble', name: 'Liquid Marble' },
  { id: 'crosshatch', name: 'Paint Fog' },
  { id: 'noise', name: 'Spray Noise' },
  { id: 'halftone', name: 'Halftone Fade' },
  { id: 'grid', name: 'Security Grid' },
  { id: 'dots', name: 'Micro Dots' },
  { id: 'hexagons', name: 'Hexagon Mesh' },
  { id: 'waves', name: 'Guilloche Waves' },
  { id: 'lines', name: 'Diagonal Texture' },
  { id: 'circuit', name: 'Tech Circuit' },
  { id: 'geometric', name: 'Geometric Shards' },
  { id: 'topo', name: 'Topographic Map' },
  { id: 'canvas', name: 'Canvas Weave' },

  // Algorithmic Security Patterns (Guilloche)
  { id: 'rosette', name: 'Banknote Rosette' },
  { id: 'spiro', name: 'Spiro Security Ring' },
  { id: 'mesh', name: 'Interference Mesh' },
  { id: 'microtext', name: 'Microtext Security' },
  { id: 'lattice', name: 'Hex Lattice' },
  { id: 'burst', name: 'Radial Burst' },
  { id: 'guilloche-dense', name: 'Dense Guilloche Star' },
  { id: 'guilloche-loop', name: 'Soft Loop Guilloche' },
  { id: 'guilloche-star', name: 'Spline Star' },
  { id: 'secure-grid', name: 'Secure Grid Interference' },
  { id: 'moire', name: 'Radial Moiré' },
  { id: 'hex-3d', name: 'Isometric 3D Cube' },
  { id: 'microtext-diag', name: 'Diagonal Microtext' },
  { id: 'fibers', name: 'Security Fibers' },
  { id: 'circuit-dense', name: 'High-Density Circuit' },
  { id: 'optic', name: 'Optical Illusion Grid' },
];

const ID_CARD_DESIGNS = [
  { id: 'none', name: 'None (Basic)', category: 'Basic' },
  
  // Modern Layout (Standard)
  { id: 'modern-indigo', name: 'Modern Indigo', category: 'Modern' },
  { id: 'modern-crimson', name: 'Modern Crimson', category: 'Modern' },
  { id: 'modern-teal', name: 'Modern Teal', category: 'Modern' },
  { id: 'modern-slate', name: 'Modern Slate', category: 'Modern' },
  { id: 'modern-violet', name: 'Modern Violet', category: 'Modern' },
  { id: 'modern-amber', name: 'Modern Amber', category: 'Modern' },
  { id: 'modern-emerald', name: 'Modern Emerald', category: 'Modern' },
  { id: 'modern-royal', name: 'Modern Royal', category: 'Modern' },
  { id: 'modern-sky', name: 'Modern Sky', category: 'Modern' },
  { id: 'modern-rose', name: 'Modern Rose', category: 'Modern' },
  
  // Sidebar Layout (Left Bar)
  { id: 'sidebar-navy', name: 'Sidebar Navy', category: 'Sidebar' },
  { id: 'sidebar-maroon', name: 'Sidebar Maroon', category: 'Sidebar' },
  { id: 'sidebar-forest', name: 'Sidebar Forest', category: 'Sidebar' },
  { id: 'sidebar-charcoal', name: 'Sidebar Charcoal', category: 'Sidebar' },
  { id: 'sidebar-plum', name: 'Sidebar Plum', category: 'Sidebar' },
  { id: 'sidebar-bronze', name: 'Sidebar Bronze', category: 'Sidebar' },
  { id: 'sidebar-ocean', name: 'Sidebar Ocean', category: 'Sidebar' },
  { id: 'sidebar-berry', name: 'Sidebar Berry', category: 'Sidebar' },
  { id: 'sidebar-gold', name: 'Sidebar Gold', category: 'Sidebar' },
  { id: 'sidebar-steel', name: 'Sidebar Steel', category: 'Sidebar' },

  // Hero Layout (Header Bar)
  { id: 'hero-cobalt', name: 'Hero Cobalt', category: 'Hero' },
  { id: 'hero-ruby', name: 'Hero Ruby', category: 'Hero' },
  { id: 'hero-pine', name: 'Hero Pine', category: 'Hero' },
  { id: 'hero-graphite', name: 'Hero Graphite', category: 'Hero' },
  { id: 'hero-amethyst', name: 'Hero Amethyst', category: 'Hero' },
  { id: 'hero-copper', name: 'Hero Copper', category: 'Hero' },
  { id: 'hero-azure', name: 'Hero Azure', category: 'Hero' },
  { id: 'hero-coral', name: 'Hero Coral', category: 'Hero' },
  { id: 'hero-citrine', name: 'Hero Citrine', category: 'Hero' },
  { id: 'hero-silver', name: 'Hero Silver', category: 'Hero' },

  // Diagonal Layout (Geometric)
  { id: 'diagonal-midnight', name: 'Diagonal Midnight', category: 'Diagonal' },
  { id: 'diagonal-cardinal', name: 'Diagonal Cardinal', category: 'Diagonal' },
  { id: 'diagonal-moss', name: 'Diagonal Moss', category: 'Diagonal' },
  { id: 'diagonal-jet', name: 'Diagonal Jet', category: 'Diagonal' },
  { id: 'diagonal-orchid', name: 'Diagonal Orchid', category: 'Diagonal' },
  { id: 'diagonal-sand', name: 'Diagonal Sand', category: 'Diagonal' },
  { id: 'diagonal-aqua', name: 'Diagonal Aqua', category: 'Diagonal' },
  { id: 'diagonal-fuchsia', name: 'Diagonal Fuchsia', category: 'Diagonal' },
  { id: 'diagonal-lime', name: 'Diagonal Lime', category: 'Diagonal' },
  { id: 'diagonal-ash', name: 'Diagonal Ash', category: 'Diagonal' },

  // Full Layout (Split Color)
  { id: 'full-primary', name: 'Full Primary', category: 'Full' },
  { id: 'full-alert', name: 'Full Alert', category: 'Full' },
  { id: 'full-nature', name: 'Full Nature', category: 'Full' },
  { id: 'full-dark', name: 'Full Dark', category: 'Full' },
  { id: 'full-luxury', name: 'Full Luxury', category: 'Full' },
  { id: 'full-earth', name: 'Full Earth', category: 'Full' },
  { id: 'full-water', name: 'Full Water', category: 'Full' },
  { id: 'full-fire', name: 'Full Fire', category: 'Full' },
  { id: 'full-air', name: 'Full Air', category: 'Full' },
  { id: 'full-metal', name: 'Full Metal', category: 'Full' },

  // University Layout (Top US Styles)
  { id: 'uni-harvard', name: 'Ivy Crimson (Harvard Style)', category: 'University' },
  { id: 'uni-yale', name: 'Ivy Blue (Yale Style)', category: 'University' },
  { id: 'uni-princeton', name: 'Ivy Orange (Princeton Style)', category: 'University' },
  { id: 'uni-stanford', name: 'Tech Cardinal (Stanford Style)', category: 'University' },
  { id: 'uni-mit', name: 'Tech Red (MIT Style)', category: 'University' },
];

type LayoutType = 'Modern' | 'Sidebar' | 'Hero' | 'Diagonal' | 'Full' | 'University' | 'None';

interface ColorTheme {
  primary: string;
  secondary: string;
  text: string;
  textSecondary: string;
  bg: string;
  accent: string;
}

// Complete Color Registry
const COLORS: Record<string, ColorTheme> = {
  // Standard Colors
  indigo: { primary: '#312e81', secondary: '#4338ca', text: '#1e1b4b', textSecondary: '#6366f1', bg: '#ffffff', accent: '#e0e7ff' },
  crimson: { primary: '#881337', secondary: '#be123c', text: '#4c0519', textSecondary: '#fb7185', bg: '#fff1f2', accent: '#ffe4e6' },
  teal: { primary: '#115e59', secondary: '#0f766e', text: '#042f2e', textSecondary: '#2dd4bf', bg: '#f0fdfa', accent: '#ccfbf1' },
  slate: { primary: '#0f172a', secondary: '#334155', text: '#020617', textSecondary: '#94a3b8', bg: '#f8fafc', accent: '#e2e8f0' },
  violet: { primary: '#4c1d95', secondary: '#6d28d9', text: '#2e1065', textSecondary: '#a78bfa', bg: '#f5f3ff', accent: '#ede9fe' },
  amber: { primary: '#78350f', secondary: '#b45309', text: '#451a03', textSecondary: '#f59e0b', bg: '#fffbeb', accent: '#fef3c7' },
  emerald: { primary: '#064e3b', secondary: '#059669', text: '#022c22', textSecondary: '#34d399', bg: '#ecfdf5', accent: '#d1fae5' },
  royal: { primary: '#1e3a8a', secondary: '#2563eb', text: '#172554', textSecondary: '#60a5fa', bg: '#eff6ff', accent: '#dbeafe' },
  sky: { primary: '#0c4a6e', secondary: '#0284c7', text: '#082f49', textSecondary: '#38bdf8', bg: '#f0f9ff', accent: '#e0f2fe' },
  rose: { primary: '#881337', secondary: '#e11d48', text: '#4c0519', textSecondary: '#fb7185', bg: '#fff1f2', accent: '#ffe4e6' },
  
  // High Contrast / Specific Themes
  navy: { primary: '#0f172a', secondary: '#1e293b', text: '#0f172a', textSecondary: '#94a3b8', bg: '#ffffff', accent: '#cbd5e1' },
  maroon: { primary: '#7f1d1d', secondary: '#991b1b', text: '#7f1d1d', textSecondary: '#fca5a5', bg: '#ffffff', accent: '#fecaca' },
  forest: { primary: '#14532d', secondary: '#166534', text: '#14532d', textSecondary: '#86efac', bg: '#ffffff', accent: '#bbf7d0' },
  charcoal: { primary: '#18181b', secondary: '#27272a', text: '#18181b', textSecondary: '#a1a1aa', bg: '#ffffff', accent: '#e4e4e7' },
  plum: { primary: '#581c87', secondary: '#6b21a8', text: '#581c87', textSecondary: '#d8b4fe', bg: '#ffffff', accent: '#e9d5ff' },
  bronze: { primary: '#431407', secondary: '#7c2d12', text: '#431407', textSecondary: '#fdba74', bg: '#ffffff', accent: '#ffedd5' },
  ocean: { primary: '#164e63', secondary: '#155e75', text: '#164e63', textSecondary: '#67e8f9', bg: '#ffffff', accent: '#cffafe' },
  berry: { primary: '#831843', secondary: '#9d174d', text: '#831843', textSecondary: '#f472b6', bg: '#ffffff', accent: '#fbcfe8' },
  gold: { primary: '#713f12', secondary: '#854d0e', text: '#422006', textSecondary: '#fde047', bg: '#ffffff', accent: '#fef08a' },
  steel: { primary: '#374151', secondary: '#4b5563', text: '#374151', textSecondary: '#9ca3af', bg: '#ffffff', accent: '#e5e7eb' },
  cobalt: { primary: '#1e40af', secondary: '#1d4ed8', text: '#1e40af', textSecondary: '#60a5fa', bg: '#ffffff', accent: '#dbeafe' },
  ruby: { primary: '#9f1239', secondary: '#be123c', text: '#881337', textSecondary: '#f43f5e', bg: '#ffffff', accent: '#ffe4e6' },
  pine: { primary: '#166534', secondary: '#15803d', text: '#14532d', textSecondary: '#4ade80', bg: '#ffffff', accent: '#dcfce7' },
  graphite: { primary: '#334155', secondary: '#475569', text: '#0f172a', textSecondary: '#94a3b8', bg: '#ffffff', accent: '#f1f5f9' },
  amethyst: { primary: '#6b21a8', secondary: '#7e22ce', text: '#581c87', textSecondary: '#c084fc', bg: '#ffffff', accent: '#f3e8ff' },
  copper: { primary: '#9a3412', secondary: '#c2410c', text: '#7c2d12', textSecondary: '#fb923c', bg: '#ffffff', accent: '#ffedd5' },
  azure: { primary: '#0369a1', secondary: '#0ea5e9', text: '#0c4a6e', textSecondary: '#7dd3fc', bg: '#ffffff', accent: '#e0f2fe' },
  coral: { primary: '#be185d', secondary: '#db2777', text: '#831843', textSecondary: '#f472b6', bg: '#ffffff', accent: '#fce7f3' },
  citrine: { primary: '#a16207', secondary: '#ca8a04', text: '#713f12', textSecondary: '#facc15', bg: '#ffffff', accent: '#fef9c3' },
  silver: { primary: '#475569', secondary: '#64748b', text: '#1e293b', textSecondary: '#cbd5e1', bg: '#ffffff', accent: '#f8fafc' },
  midnight: { primary: '#172554', secondary: '#1e3a8a', text: '#0f172a', textSecondary: '#60a5fa', bg: '#eff6ff', accent: '#bfdbfe' },
  cardinal: { primary: '#7f1d1d', secondary: '#991b1b', text: '#450a0a', textSecondary: '#fca5a5', bg: '#fef2f2', accent: '#fecaca' },
  moss: { primary: '#14532d', secondary: '#166534', text: '#052e16', textSecondary: '#4ade80', bg: '#f0fdf4', accent: '#bbf7d0' },
  jet: { primary: '#000000', secondary: '#18181b', text: '#000000', textSecondary: '#71717a', bg: '#f9fafb', accent: '#e5e7eb' },
  orchid: { primary: '#581c87', secondary: '#7e22ce', text: '#3b0764', textSecondary: '#d8b4fe', bg: '#faf5ff', accent: '#e9d5ff' },
  sand: { primary: '#78350f', secondary: '#b45309', text: '#451a03', textSecondary: '#f59e0b', bg: '#fffbeb', accent: '#fde68a' },
  aqua: { primary: '#0e7490', secondary: '#06b6d4', text: '#155e75', textSecondary: '#67e8f9', bg: '#ecfeff', accent: '#cffafe' },
  fuchsia: { primary: '#701a75', secondary: '#a21caf', text: '#4a044e', textSecondary: '#e879f9', bg: '#fdf4ff', accent: '#f5d0fe' },
  lime: { primary: '#3f6212', secondary: '#65a30d', text: '#1a2e05', textSecondary: '#a3e635', bg: '#f7fee7', accent: '#d9f99d' },
  ash: { primary: '#374151', secondary: '#4b5563', text: '#111827', textSecondary: '#9ca3af', bg: '#f3f4f6', accent: '#d1d5db' },

  // University Replicas
  harvard: { primary: '#A51C30', secondary: '#292929', text: '#1e1e1e', textSecondary: '#A51C30', bg: '#ffffff', accent: '#F3F4F6' },
  yale: { primary: '#00356B', secondary: '#286dc0', text: '#222222', textSecondary: '#00356B', bg: '#ffffff', accent: '#BDD6E6' },
  princeton: { primary: '#E87722', secondary: '#000000', text: '#000000', textSecondary: '#E87722', bg: '#ffffff', accent: '#F58025' },
  stanford: { primary: '#8C1515', secondary: '#4d4f53', text: '#2e2d29', textSecondary: '#8C1515', bg: '#ffffff', accent: '#B83A4B' },
  mit: { primary: '#A31F34', secondary: '#8A8B8C', text: '#000000', textSecondary: '#A31F34', bg: '#ffffff', accent: '#C2C0BF' },

  // Full Layout Colors
  primary: { primary: '#2563eb', secondary: '#3b82f6', text: '#172554', textSecondary: '#bfdbfe', bg: '#1e40af', accent: '#60a5fa' },
  alert: { primary: '#dc2626', secondary: '#ef4444', text: '#450a0a', textSecondary: '#fecaca', bg: '#991b1b', accent: '#f87171' },
  nature: { primary: '#16a34a', secondary: '#22c55e', text: '#052e16', textSecondary: '#bbf7d0', bg: '#15803d', accent: '#4ade80' },
  dark: { primary: '#18181b', secondary: '#27272a', text: '#000000', textSecondary: '#a1a1aa', bg: '#09090b', accent: '#52525b' },
  luxury: { primary: '#d97706', secondary: '#f59e0b', text: '#451a03', textSecondary: '#fde68a', bg: '#b45309', accent: '#fbbf24' },
  earth: { primary: '#57534e', secondary: '#78716c', text: '#292524', textSecondary: '#d6d3d1', bg: '#44403c', accent: '#a8a29e' },
  water: { primary: '#0891b2', secondary: '#06b6d4', text: '#164e63', textSecondary: '#cffafe', bg: '#0e7490', accent: '#22d3ee' },
  fire: { primary: '#ea580c', secondary: '#f97316', text: '#7c2d12', textSecondary: '#ffedd5', bg: '#c2410c', accent: '#fb923c' },
  air: { primary: '#0ea5e9', secondary: '#38bdf8', text: '#082f49', textSecondary: '#e0f2fe', bg: '#0284c7', accent: '#7dd3fc' },
  metal: { primary: '#475569', secondary: '#64748b', text: '#0f172a', textSecondary: '#cbd5e1', bg: '#334155', accent: '#94a3b8' },
};

const getIdDesign = (designId: string) => {
    if (designId === 'none') return { layout: 'None', theme: COLORS.slate };
    const [layoutKey, colorKey] = designId.split('-');
    
    let layout: LayoutType = 'Modern';
    if(layoutKey === 'sidebar') layout = 'Sidebar';
    if(layoutKey === 'hero') layout = 'Hero';
    if(layoutKey === 'diagonal') layout = 'Diagonal';
    if(layoutKey === 'full') layout = 'Full';
    if(layoutKey === 'uni') layout = 'University' as any;

    const theme = COLORS[colorKey] || COLORS.indigo;
    return { layout, theme };
};




// ==========================================
// 3. MAIN COMPONENT
// ==========================================

export const IdCardDesigner: React.FC<IdCardDesignerProps> = ({ data: propData, onUpdateData, initialConfig, onConfigChange }) => {
  // State
  const [data, setData] = useState(propData);

  const [designId, setDesignId] = useState(initialConfig?.designId || ID_CARD_DESIGNS[1].id);
  const [pattern, setPattern] = useState(initialConfig?.pattern || ID_PATTERNS[9].id); // Grid default
  const [patternOpacity, setPatternOpacity] = useState(initialConfig?.patternOpacity ?? 0.15);
  const [patternColor, setPatternColor] = useState(initialConfig?.patternColor || '#000000');
  const [customPrimaryColor, setCustomPrimaryColor] = useState<string | null>(initialConfig?.customPrimaryColor || null);
  const [customTextColor, setCustomTextColor] = useState<string | null>(initialConfig?.customTextColor || null);
  const [customBackground, setCustomBackground] = useState<string | null>(initialConfig?.customBackground || null);

  // Sync with prop updates
  useEffect(() => {
    setData(propData);
  }, [propData]);

  const currentConfig: IdCardConfig = {
    designId,
    pattern,
    patternOpacity,
    patternColor,
    customPrimaryColor: customPrimaryColor || null,
    customTextColor: customTextColor || null,
    customBackground
  };

  // Sync with initialConfig changes
  useEffect(() => {
    if (initialConfig) {
      setDesignId(initialConfig.designId);
      setPattern(initialConfig.pattern);
      setPatternOpacity(initialConfig.patternOpacity);
      setPatternColor(initialConfig.patternColor);
      setCustomPrimaryColor(initialConfig.customPrimaryColor);
      setCustomTextColor(initialConfig.customTextColor);
      setCustomBackground(initialConfig.customBackground);
    }
  }, [initialConfig]);

  // Notify parent of changes
  useEffect(() => {
    if (onConfigChange) {
      onConfigChange({
        designId,
        pattern,
        patternOpacity,
        patternColor,
        customPrimaryColor,
        customTextColor,
        customBackground
      });
    }
  }, [designId, pattern, patternOpacity, patternColor, customPrimaryColor, customTextColor, customBackground, onConfigChange]);
  


  const { theme: baseTheme } = getIdDesign(designId);

  const fileInputRef = useRef<HTMLInputElement>(null);



  // --- ACTIONS ---

  const handlePrint = () => {
    window.print();
  };

  const handleUploadBackground = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomBackground(reader.result as string);
        if (patternOpacity < 0.2) setPatternOpacity(0.5);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLoadMockData = () => {
      const mockData: StudentData = {
        ...data,
        name: "Sarah J. Connor",
        studentId: "10249582",
        gradeLevel: "Senior Year",
        academicYear: "2024 - 2025",
        schoolName: "Westside Institute of Technology",
        schoolAddress: "123 Academic Ave, Tech Valley, CA",
        schoolContact: "(555) 987-6543",
        studentUid: "UID-SAMPLE-123456",
        admissionDate: "2024-01-15",
        programDuration: 4, // Explicit Total Levels (e.g. Form 1-4)
        dob: "2006-08-12",
        photoUrl: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
      };
      
      setData(mockData);
      if(onUpdateData) {
          onUpdateData(mockData);
      }
  };

  const handlePrevDesign = () => {
    const currentIndex = ID_CARD_DESIGNS.findIndex(d => d.id === designId);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : ID_CARD_DESIGNS.length - 1;
    setDesignId(ID_CARD_DESIGNS[prevIndex].id);
  };

  const handleNextDesign = () => {
    const currentIndex = ID_CARD_DESIGNS.findIndex(d => d.id === designId);
    const nextIndex = currentIndex < ID_CARD_DESIGNS.length - 1 ? currentIndex + 1 : 0;
    setDesignId(ID_CARD_DESIGNS[nextIndex].id);
  };





  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 h-full relative">
        <style>{`
            @media print {
                body * {
                    visibility: hidden;
                }
                #printable-section, #printable-section * {
                    visibility: visible;
                }
                #printable-section {
                    display: flex !important;
                    position: fixed;
                    left: 0;
                    top: 0;
                    width: 100vw;
                    height: 100vh;
                    margin: 0;
                    background: white;
                    z-index: 100000;
                    flex-direction: row;
                    flex-wrap: wrap;
                    align-content: center;
                    align-items: center;
                    justify-content: center;
                    gap: 40px;
                    padding: 20px;
                }
                /* Hide scrollbars */
                ::-webkit-scrollbar { display: none; }
                
                /* FORCE BACKGROUND COLORS */
                * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
            }
        `}</style>

        {/* --- PRINT ONLY SECTION --- */}
        <div id="printable-section" className="hidden">
            <div className="flex flex-col items-center gap-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Front</span>
                <IdCardRenderer data={data} config={currentConfig} side="front" />
            </div>
            <div className="flex flex-col items-center gap-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Back</span>
                <IdCardRenderer data={data} config={currentConfig} side="back" />
            </div>
        </div>

        {/* --- LEFT SIDEBAR (CONTROLS) --- */}
        <div className="lg:col-span-4 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 h-full overflow-y-auto p-6 space-y-6 print:hidden">
             <div className="flex items-center justify-between">
                 <h2 className="text-lg font-bold flex items-center gap-2"><UserSquare className="text-indigo-600" /> Card Designer</h2>
                 <button 
                    onClick={handleLoadMockData} 
                    className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded hover:bg-indigo-100" 
                    title="Load sample data"
                 >
                    Mock Data
                 </button>
             </div>

             {/* Design Selector */}
             <div>
                 <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">Layout Style</label>
                 <div className="flex gap-2">
                     <button 
                         onClick={handlePrevDesign}
                         className="p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors"
                         title="Previous Design"
                     >
                         <ChevronLeft size={16} />
                     </button>
                     <div className="relative flex-1">
                         <select 
                            value={designId} 
                            onChange={(e) => setDesignId(e.target.value)} 
                            className="w-full p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium appearance-none outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-600 dark:text-slate-300 shadow-sm transition-all hover:border-slate-300 dark:hover:border-slate-600"
                         >
                             {ID_CARD_DESIGNS.map(d => <option key={d.id} value={d.id} className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">{d.name}</option>)}
                         </select>
                         <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                     </div>
                     <button 
                         onClick={handleNextDesign}
                         className="p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors"
                         title="Next Design"
                     >
                         <ChevronRight size={16} />
                     </button>
                 </div>
             </div>

             {/* Pattern Controls */}
             <div className="space-y-4 border-t border-slate-200 dark:border-slate-800 pt-4">
                 <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"><Sliders size={14} /> Background Pattern</div>
                 <div className="grid grid-cols-2 gap-3">
                     <div>
                         <label className="text-[10px] text-slate-400 dark:text-slate-500 mb-1 block">Texture</label>
                         <div className="relative">
                             <select 
                                value={pattern} 
                                onChange={(e) => setPattern(e.target.value)} 
                                className="w-full p-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium appearance-none outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-600 dark:text-slate-300 shadow-sm transition-all hover:border-slate-300 dark:hover:border-slate-600"
                             >
                                 {ID_PATTERNS.map(p => <option key={p.id} value={p.id} className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">{p.name}</option>)}
                             </select>
                             <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                         </div>
                     </div>
                     <div>
                         <label className="text-[10px] text-slate-400 dark:text-slate-500 mb-1.5 block font-medium">Opacity: {Math.round(patternOpacity * 100)}%</label>
                         <input 
                            type="range" 
                            min="0" 
                            max="1" 
                            step="0.05" 
                            value={patternOpacity} 
                            onChange={(e) => setPatternOpacity(parseFloat(e.target.value))} 
                            className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/20" 
                         />
                     </div>
                 </div>
                 <div className="grid grid-cols-3 gap-3">
                     <div>
                         <label className="text-[10px] text-slate-400 dark:text-slate-500 mb-1.5 block flex items-center gap-1 font-medium"><Droplet size={10}/> Pattern</label>
                         <div className="relative h-9 w-full rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm cursor-pointer group hover:border-indigo-400 transition-colors">
                             <div className="absolute inset-0" style={{ backgroundColor: patternColor }} />
                             <input type="color" value={patternColor} onChange={(e) => setPatternColor(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                         </div>
                     </div>
                     <div>
                         <label className="text-[10px] text-slate-400 dark:text-slate-500 mb-1.5 block flex items-center gap-1 font-medium"><Palette size={10}/> Primary</label>
                         <div className="relative h-9 w-full rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm cursor-pointer group hover:border-indigo-400 transition-colors">
                             <div className="absolute inset-0" style={{ backgroundColor: customPrimaryColor || baseTheme.primary }} />
                             <input type="color" value={customPrimaryColor || baseTheme.primary} onChange={(e) => setCustomPrimaryColor(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                         </div>
                     </div>
                     <div>
                         <label className="text-[10px] text-slate-400 dark:text-slate-500 mb-1.5 block flex items-center gap-1 font-medium"><Type size={10}/> Text</label>
                         <div className="relative h-9 w-full rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm cursor-pointer group hover:border-indigo-400 transition-colors">
                             <div className="absolute inset-0" style={{ backgroundColor: customTextColor || baseTheme.text }} />
                             <input type="color" value={customTextColor || baseTheme.text} onChange={(e) => setCustomTextColor(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                         </div>
                     </div>
                 </div>
             </div>

             {/* Background Image Controls */}
             <div className="space-y-4 border-t border-slate-100 dark:border-slate-800 pt-4">
                 <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"><ImagePlus size={14} /> Custom Background</div>
                 
                 {/* Upload */}
                 <div className="flex gap-2">
                     <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleUploadBackground} />
                     <button 
                        onClick={() => fileInputRef.current?.click()} 
                        className="flex-1 py-2.5 bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-600 hover:border-indigo-500 dark:hover:border-indigo-400 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md"
                     >
                         <ImagePlus size={14} /> Upload Image
                     </button>
                     {customBackground && (
                         <button onClick={() => setCustomBackground(null)} className="px-3 bg-red-50 hover:bg-red-100 text-red-600 border border-red-100 rounded-lg transition"><Trash2 size={14} /></button>
                     )}
                 </div>
             </div>

             <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                 <button 
                    onClick={handlePrint} 
                    className="w-full py-3 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-xl font-bold shadow-lg shadow-slate-900/10 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 transition-all"
                 >
                     <Printer size={18} /> Print Card
                 </button>
                 <p className="text-[10px] text-center text-slate-400 mt-3 font-medium">Standard CR80 Size (85.6mm x 54mm)</p>
             </div>
        </div>

        {/* --- RIGHT PREVIEW AREA --- */}
        <div className="lg:col-span-8 bg-slate-100 dark:bg-slate-950 flex flex-col items-center justify-center p-8 overflow-y-auto">
             <div className="flex flex-col xl:flex-row gap-6 scale-[0.8] md:scale-[0.9] xl:scale-100 transition-transform origin-center">
                 
                 {/* FRONT */}
                 <div className="flex flex-col items-center gap-2">
                     <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Front Side</span>
                     <IdCardRenderer data={data} config={currentConfig} side="front" />
                 </div>

                 {/* BACK */}
                 <div className="flex flex-col items-center gap-2">
                     <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Back Side</span>
                     <IdCardRenderer data={data} config={currentConfig} side="back" />
                 </div>

             </div>
        </div>
    </div>
  );
};
